import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@material-ui/core';
import { KlpModal, Typography } from '@klp/design';

/** Explanations longer than this are shortened in the table cell and shown in full in a modal. */
export const KLIMAGASS_FORKLARING_MAX_LENGTH = 80;

type ProsjektAvfallKlimagassForklaringProps = {
    forklaring: string | null;
    prosjektnummer: string;
    prosjektnavn: string;
};

/**
 * The project leader's explanation for why a greenhouse gas calculation is not relevant.
 * Short explanations are shown as-is; long ones are shortened with a "read more" link that
 * opens the full text in a modal (works with mouse, keyboard and touch, unlike a tooltip).
 */
export function ProsjektAvfallKlimagassForklaring({
    forklaring,
    prosjektnummer,
    prosjektnavn,
}: ProsjektAvfallKlimagassForklaringProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    if (!forklaring) {
        return <span>–</span>;
    }

    if (forklaring.length <= KLIMAGASS_FORKLARING_MAX_LENGTH) {
        return <span>{forklaring}</span>;
    }

    const shortened = forklaring
        .slice(0, KLIMAGASS_FORKLARING_MAX_LENGTH)
        .trimEnd();

    return (
        <>
            <span>{`${shortened}…`}</span>{' '}
            {/* A link-styled <button>: reachable with Tab, activated with Enter/Space */}
            <Link
                component="button"
                variant="body2"
                onClick={() => setIsOpen(true)}
            >
                {t('sustainability.prosjektAvfall.lesMer')}
            </Link>
            <KlpModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                titleString={`${prosjektnummer} ${prosjektnavn}`}
            >
                <Typography
                    translation="sustainability.prosjektAvfall.klimagassForklaring"
                    klpTypographyVariant="small"
                />
                <Typography style={{ whiteSpace: 'pre-wrap' }}>
                    {forklaring}
                </Typography>
            </KlpModal>
        </>
    );
}
